/** Grade classification for the map's grade-colored route line — pure, no
 * rendering. Thresholds mirror ui/ProfileStrip.tsx's segmentColor exactly
 * so the map line and the elevation profile's band never disagree about
 * what counts as easy/moderate/steep. */

import type { ElevationSample, LonLat } from "./types";

export type GradeClass = "easy" | "moderate" | "steep" | "nodata";

/** null (no usable sample pair) -> nodata; < 6% -> easy; < 12% -> moderate;
 * else steep. EXACT phone thresholds — see ProfileStrip.segmentColor. */
export function gradeClass(gradePct: number | null): GradeClass {
  if (gradePct === null) return "nodata";
  if (gradePct < 6) return "easy";
  if (gradePct < 12) return "moderate";
  return "steep";
}

export interface GradeRun {
  cls: GradeClass;
  coords: LonLat[];
}

/** Classifies each consecutive sample pair by |Δele|/Δdist × 100 (Δdist ≤
 * 0 — two samples at the same distance — can't derive a grade, so nodata),
 * then merges consecutive same-class pairs into one run each, so the map
 * layer paints a handful of runs rather than one tiny segment per sample.
 * Adjacent runs share their boundary coordinate (the new run starts on the
 * same point the previous one ended on) so two differently-colored runs
 * painted back to back read as one continuous line, not a broken one.
 * Fewer than 2 samples can't form a single pair, so []. */
export function gradeRuns(samples: ElevationSample[]): GradeRun[] {
  if (samples.length < 2) return [];

  const runs: GradeRun[] = [];
  for (let i = 1; i < samples.length; i++) {
    const a = samples[i - 1];
    const b = samples[i];
    const dDist = b.distM - a.distM;
    const gradePct = dDist > 0 ? (Math.abs(b.eleM - a.eleM) / dDist) * 100 : null;
    const cls = gradeClass(gradePct);

    const last = runs[runs.length - 1];
    if (last && last.cls === cls) {
      last.coords.push(b.at);
    } else {
      runs.push({ cls, coords: [a.at, b.at] });
    }
  }
  return runs;
}
